import { useCapabilities } from '@/features/authentication/hooks/useCapabilities';
import {
  organizationListVariants,
  organizationScrollAreaVariants,
  organizationSearchWrapperVariants,
  organizationSectionLabelVariants,
  organizationTreeChevronBoxVariants,
  organizationTreeMarkVariants,
  organizationTreeNameVariants,
  organizationTreeRowVariants,
} from '@/features/organization-project-picker/config/organizationTree';
import { useOrganizationTree } from '@/features/organization-project-picker/hooks/useOrganizationTree';
import { useOrganizationTreeVirtualizer } from '@/features/organization-project-picker/hooks/useOrganizationTreeVirtualizer';
import { getOrganizationTreeRows } from '@/features/organization-project-picker/lib/organizationTree';
import {
  useOrganizations,
  useProject,
} from '@/features/organization-project-picker/services/organizationProjectQueries';
import { SupervisorActivityOrganizationTreeRow } from '@/features/supervisor/components/SupervisorActivityOrganizationTreeRow';
import { supervisorActivityScopeSearch } from '@/features/supervisor/lib/activityScopeSearch';
import type { SupervisorActivitySearch } from '@/features/supervisor/types/supervisor';
import { NavSearch } from '@/shared/components/AppSidebar/NavSearch';
import { NavSectionLabel } from '@/shared/components/AppSidebar/NavSectionLabel';
import { SidebarListSkeleton } from '@/shared/components/AppSidebar/SidebarListSkeleton';
import { ListFailed, ListNextPageFooter } from '@/shared/components/ListStatus';
import { NavEmpty } from '@/shared/components/ui/empty';
import { useDebouncedValue } from '@/shared/hooks/useDebouncedValue';
import { flattenInfiniteItems } from '@/shared/lib/infiniteQuery';
import { Trans, useLingui } from '@lingui/react/macro';
import { Link } from '@tanstack/react-router';
import { Building2 } from 'lucide-react';
import { useDeferredValue, useMemo, useState } from 'react';

/** Organization and project filter tree owned by the Field activity rail. */
export function SupervisorActivityOrganizationTree({
  search,
}: { search: SupervisorActivitySearch }) {
  const { t } = useLingui();
  const { hasCapability } = useCapabilities();
  const withProjects = hasCapability('projects.list');
  const activeOrganizationId =
    search.organizationId?.length === 1 ? search.organizationId[0] : undefined;
  const activeProjectId = search.projectId;
  const [query, setQuery] = useState('');
  const term = useDebouncedValue(query.trim(), 300);
  const deferredTerm = useDeferredValue(term);
  const organizationsQuery = useOrganizations(deferredTerm || undefined);
  const {
    data: organizationPages,
    isPending: organizationsPending,
    isError: organizationsError,
    error: organizationsQueryError,
    refetch: refetchOrganizations,
  } = organizationsQuery;
  const organizations = useMemo(() => flattenInfiniteItems(organizationPages), [organizationPages]);
  const [expandedOrganizationIds, setExpandedOrganizationIds] = useState<Set<string>>(
    () => new Set(withProjects && activeOrganizationId ? [activeOrganizationId] : []),
  );
  const visibleExpandedOrganizationIds = useMemo(
    () => (withProjects ? expandedOrganizationIds : new Set<string>()),
    [expandedOrganizationIds, withProjects],
  );
  const { data: activeProject } = useProject(activeProjectId ?? '');
  const { tree, getProjectQuery, retryProjectBranch, fetchNextProjectPage } = useOrganizationTree(
    organizations,
    visibleExpandedOrganizationIds,
    activeProject?.organizationId === activeOrganizationId ? activeProject : undefined,
  );
  const treeRows = useMemo(() => getOrganizationTreeRows(tree), [tree]);
  const { scrollRef, treeListRef, scrollMargin, virtualizer } = useOrganizationTreeVirtualizer({
    rows: treeRows,
    query: organizationsQuery,
    density: 'compact',
    fetchNextProjectPage,
  });
  const allOrganizations = !search.organizationId?.length;
  const totalCount = organizationPages?.pages[0]?.totalCount ?? organizations.length;

  return (
    <>
      <div className={organizationSearchWrapperVariants()}>
        <NavSearch
          value={query}
          onChange={setQuery}
          placeholder={t`Search…`}
          ariaLabel={t`Search organizations`}
          size="compact"
        />
      </div>
      <section
        ref={scrollRef}
        aria-label={t`Organizations`}
        // biome-ignore lint/a11y/noNoninteractiveTabindex: this is the sole scroll viewport and must accept keyboard scrolling.
        tabIndex={0}
        className={organizationScrollAreaVariants()}
      >
        <div className={organizationSectionLabelVariants()}>
          <NavSectionLabel count={totalCount} size="compact">
            <Trans>Organizations</Trans>
          </NavSectionLabel>
        </div>
        {organizationsPending && !organizationPages ? (
          <SidebarListSkeleton />
        ) : organizationsError && !organizationPages ? (
          <ListFailed error={organizationsQueryError} retry={() => void refetchOrganizations()} />
        ) : (
          <>
            <Link
              from="/supervisor/team/field-activity"
              to="/supervisor/team/field-activity"
              search={(previous) => supervisorActivityScopeSearch(previous)}
              replace
              aria-current={allOrganizations ? 'page' : undefined}
              data-active={allOrganizations}
              className={organizationTreeRowVariants({ active: allOrganizations })}
            >
              <span aria-hidden className={organizationTreeChevronBoxVariants()} />
              <span
                aria-hidden
                className={organizationTreeMarkVariants({ active: allOrganizations })}
              >
                <Building2 className="size-3" />
              </span>
              <span className={organizationTreeNameVariants({ active: allOrganizations })}>
                <Trans>All organizations</Trans>
              </span>
            </Link>
            {organizations.length === 0 ? (
              <NavEmpty>
                <Trans>No organizations</Trans>
              </NavEmpty>
            ) : null}
            <ul
              ref={treeListRef}
              className={organizationListVariants({ searching: term !== deferredTerm })}
              style={{ height: virtualizer.getTotalSize() }}
            >
              {virtualizer.getVirtualItems().map((virtualRow) => {
                const row = treeRows[virtualRow.index];
                if (!row) return null;
                return (
                  <SupervisorActivityOrganizationTreeRow
                    key={row.key}
                    row={row}
                    activeOrganizationId={activeOrganizationId}
                    activeProjectId={activeProjectId}
                    withProjects={withProjects}
                    onExpandedChange={(organizationId, expanded) => {
                      setExpandedOrganizationIds((currentIds) => {
                        const nextIds = new Set(currentIds);
                        if (expanded) nextIds.add(organizationId);
                        else nextIds.delete(organizationId);
                        return nextIds;
                      });
                    }}
                    getProjectQuery={getProjectQuery}
                    onRetryProjectBranch={retryProjectBranch}
                    measureRef={virtualizer.measureElement}
                    virtualIndex={virtualRow.index}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      transform: `translateY(${virtualRow.start - scrollMargin}px)`,
                    }}
                  />
                );
              })}
            </ul>
            <ListNextPageFooter query={organizationsQuery} />
          </>
        )}
      </section>
    </>
  );
}
